import { create } from 'zustand';
import type { PlanTier, TenantRole } from '@/types';
import { API_URL } from '@/lib/apiUrl';
import { getCsrfToken } from '@/lib/csrf';
import { NOT_LIMITED, parseLimitedAccess, type LimitedAccess } from '@/lib/limitedAccess';
import { useBuilderStore } from './builderStore';
import { clearBuildProgress } from '@/lib/buildWizardProgress';
import { clearAllInterviews } from '@/lib/interviewPersist';
// Lazy import avoids circular deps — resolved at call time
import { useIntakeStore } from '../hooks/useIntakeStore';

// Rotate the session cookie via the httpOnly refresh cookie (7-day). Raw fetch (not apiClient) to
// avoid a store↔client import cycle.
//
// ONE refresh at a time, shared by every caller in this tab — hydrate here, and the axios client's
// refresh-on-401 (api/client.ts refreshSession delegates to this). The refresh token ROTATES: two
// POSTs of the same one race, the first rotates it, and the second is answered 401 — which clears
// the cookies the first had just issued and signs the tab out. Overlapping hydrates (which the
// generation guard deliberately allows) and a 401 on an ordinary request both reach this.
//
// ACROSS tabs too: the refresh cookie is shared by every tab, so two tabs' 401s (or keep-alives)
// arriving together race exactly the same way. A Web Lock (navigator.locks) makes the refresh
// exclusive browser-wide, and the tab that waited for it does NOT rotate again when another tab
// already refreshed after it asked: the cookie jar holds that tab's new session, so it is reused
// (the time of the last successful refresh is kept in localStorage). One 401 burst across N tabs
// is one rotation. Browsers without Web Locks keep the in-tab guard, and the gateway accepts the
// just-rotated refresh token for a few seconds (routes/auth.ts, REFRESH_REUSE_GRACE_SECONDS), so
// a race there is not a logout either.
/**
 * What a refresh attempt established about the session:
 *   • 'ok' — a live session is in the cookie jar (this call rotated it, or another tab just did);
 *   • 'rejected' — the server answered that the session is over (it has cleared the cookies);
 *   • 'unavailable' — no answer about the session: offline, a 5xx, a 429, a CSRF token that went
 *     stale mid-rotation. Nothing may be cleared on this — above all not every tab's sign-in.
 */
export type RefreshOutcome = 'ok' | 'rejected' | 'unavailable';
let inflightRefresh: Promise<RefreshOutcome> | null = null;
const REFRESH_LOCK = 'bls.sessionRefresh';
/**
 * When any tab last completed a successful refresh (ms since epoch) — how a tab that waited for
 * the lock learns it no longer needs to rotate.
 *
 * Kept in IndexedDB, not localStorage: Chromium serves localStorage reads from a per-tab copy
 * that other tabs' writes reach asynchronously, on a different channel from the lock hand-over,
 * so the tab granted the lock could still read the old value and rotate again (seen in the
 * two-tab browser test). IndexedDB reads go to the one browser-side store, and the writer's
 * transaction commits before it releases the lock. localStorage remains the fallback where
 * IndexedDB is unavailable.
 */
export const REFRESHED_AT_KEY = 'bls.sessionRefreshedAt';
const SESSION_DB = 'bls-session';
let sessionDb: Promise<IDBDatabase | null> | null = null;
function openSessionDb(): Promise<IDBDatabase | null> {
  if (sessionDb) return sessionDb;
  sessionDb = new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') return resolve(null);
      const req = indexedDB.open(SESSION_DB, 1);
      req.onupgradeneeded = () => req.result.createObjectStore('kv');
      req.onsuccess = () => {
        const db = req.result;
        db.onversionchange = () => { db.close(); sessionDb = null; };
        resolve(db);
      };
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return sessionDb;
}
async function readRefreshedAt(): Promise<number> {
  const db = await openSessionDb();
  if (db) {
    const at = await new Promise<number | null>((resolve) => {
      try {
        const get = db.transaction('kv', 'readonly').objectStore('kv').get(REFRESHED_AT_KEY);
        get.onsuccess = () => resolve(Number(get.result) || 0);
        get.onerror = () => resolve(null);
      } catch {
        resolve(null);
      }
    });
    if (at !== null) return at;
  }
  try { return Number(localStorage.getItem(REFRESHED_AT_KEY)) || 0; } catch { return 0; }
}
async function noteRefreshed(at: number): Promise<void> {
  const db = await openSessionDb();
  if (db) {
    const done = await new Promise<boolean>((resolve) => {
      try {
        const tx = db.transaction('kv', 'readwrite');
        tx.objectStore('kv').put(at, REFRESHED_AT_KEY);
        tx.oncomplete = () => resolve(true); // committed BEFORE the lock is released
        tx.onerror = () => resolve(false);
        tx.onabort = () => resolve(false);
      } catch {
        resolve(false);
      }
    });
    if (done) return;
  }
  try { localStorage.setItem(REFRESHED_AT_KEY, String(at)); } catch { /* ignore */ }
}
async function postRefresh(): Promise<RefreshOutcome> {
  try {
    const res = await fetch(`${API_URL}/auth/refresh/cookie`, {
      method: 'POST',
      credentials: 'include',
      // Read at send time, inside the lock: a refresh another tab just completed rotated it.
      //
      // No `Content-Type: application/json`: this POST has no body, and Fastify answers a JSON
      // content type with an empty body 400 FST_ERR_CTP_EMPTY_JSON_BODY before the route runs.
      // That 400 reads as 'unavailable', so every refresh failed — the keep-alive silently, and
      // any request after the access token lapsed with "We could not renew your session".
      headers: { 'X-CSRF-Token': getCsrfToken(false) ?? '' },
    });
    if (res.ok) return 'ok';
    if (res.status === 401) return 'rejected';
    if (res.status === 403) {
      // The refresh route's CSRF check runs first. A token that went STALE because another tab
      // rotated it mid-flight ("Invalid CSRF token") says nothing about the session. A MISSING one
      // means the cookies are gone — as signed out as "no active tenant".
      const body = (await res.json().catch(() => null)) as { error?: unknown } | null;
      return /invalid csrf/i.test(String(body?.error ?? '')) ? 'unavailable' : 'rejected';
    }
    return 'unavailable';
  } catch {
    return 'unavailable';
  }
}
async function refreshAfter(since: number): Promise<RefreshOutcome> {
  // A refresh (by any tab) completed after the request that needed one was sent — typically while
  // this tab waited for the lock. The shared cookie jar already carries a session newer than the
  // one that was refused; rotating it again is at best a wasted rotation.
  if ((await readRefreshedAt()) >= since) return 'ok';
  const outcome = await postRefresh();
  if (outcome === 'ok') await noteRefreshed(Date.now());
  return outcome;
}
interface WebLocks { request(name: string, cb: () => Promise<RefreshOutcome>): Promise<RefreshOutcome> }
/**
 * `since`: when the request that came back 401 was SENT (default: now). Any refresh completed
 * after that is good enough — so a retry, or a tab that queued behind another's refresh, reuses it.
 */
export function refreshSessionOutcome(since: number = Date.now()): Promise<RefreshOutcome> {
  if (inflightRefresh) return inflightRefresh;
  const locks = typeof navigator !== 'undefined'
    ? (navigator as unknown as { locks?: WebLocks }).locks
    : undefined;
  const run: Promise<RefreshOutcome> = (locks
    ? locks.request(REFRESH_LOCK, () => refreshAfter(since))
    : refreshAfter(since))
    .catch((): RefreshOutcome => 'unavailable')
    .finally(() => { inflightRefresh = null; });
  inflightRefresh = run;
  return run;
}
/** The same, for callers that only need "is there a session to retry with". */
export function refreshSessionCookie(): Promise<boolean> {
  return refreshSessionOutcome().then((o) => o === 'ok');
}

/**
 * Cookie-auth store (Wave 2.4 cutover).
 *
 * There is no JS-accessible access token any more — the session lives in httpOnly
 * cookies the browser sends automatically. Identity is hydrated from GET /auth/me,
 * NOT decoded from a token in JS. `status` is a three-state machine so the Router
 * can show a loading shell until hydration resolves (avoids a login-flash on reload).
 */

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

interface MeResponse {
  user: { id: string; email: string; role: string } | null;
  tenant: { id: string; slug: string; plan: string; status: string; blt_enabled?: boolean; html_access?: boolean; site_plan?: string | null } | null;
  role: string | null;
  /** Set by the gateway when this request carried a valid impersonation cookie. */
  impersonating?: boolean;
  /** Managed site / Contributor limits (lib/limitedAccess.ts). */
  limited_access?: unknown;
}

interface AuthState {
  status: AuthStatus;
  /** Derived convenience flag (status === 'authenticated'). */
  isAuthenticated: boolean;
  /**
   * True while an admin is impersonating a tenant user. Selects the CSRF cookie, and drives the
   * banner that says whose account is on screen.
   *
   * HYDRATED FROM THE SERVER, not remembered. It used to be set only at impersonate-start and
   * kept in memory, so a reload dropped it while the cookie lived on for its full hour: the
   * client then sent the session CSRF token on every mutation (all of which failed), and nothing
   * on screen could say the account being edited was not the admin's own.
   */
  impersonating: boolean;

  userId: string | null;
  tenantId: string | null;
  tenantSlug: string | null;
  role: TenantRole | null;
  /** System-level role: 'user' | 'superadmin' (from /auth/me user.role). */
  userRole: string | null;
  plan: PlanTier | null;
  /**
   * The plan the account's SITE shows (GET /auth/me `tenant.site_plan`): 'launch' for an unpaid
   * MyStateMLS-launched account (Free, plus About, Contact and the home page's own listings), else
   * the same as `plan`. Use it only for what the site shows: pages, menu links and sections.
   */
  sitePlan: string | null;
  /**
   * Whether BLT is on for this tenant: the gateway's answer (GET /auth/me `tenant.blt_enabled`,
   * from the same read that gates the assistant routes). The Studio's BLT tab reads this alone
   * (lib/bltChatFlag.ts) — the builder never works it out from the plan.
   */
  bltEnabled: boolean;
  /**
   * Whether this person may add or change HTML (a Raw HTML block, a page's own code): the
   * gateway's answer (GET /auth/me `tenant.html_access`, services/api-gateway/src/lib/html-access.ts),
   * the same rule the page saves refuse with. Staff and accounts given HTML access only.
   */
  htmlAccess: boolean;
  /**
   * Managed site / Contributor limits: the gateway's answer (GET /auth/me `limited_access`,
   * services/api-gateway/src/lib/limited-access.ts). For the account's primary site; the layout
   * re-asks for the site being edited (hooks/useLimitedAccess.ts).
   */
  limitedAccess: LimitedAccess;
  email: string | null;
  /**
   * Multi-site: the site currently being edited (the `/studio/:siteId` URL drives it). Sent as
   * the `X-Site-Id` header on every API call so the gateway scopes to this site. null = the
   * tenant's primary site (the gateway falls back to it when the header is absent).
   */
  activeSiteId: string | null;

  /** Fetch /auth/me and populate identity. Resolves to true when authenticated. */
  hydrate: () => Promise<boolean>;
  setImpersonating: (value: boolean) => void;
  /** Set the active site being edited (null → the tenant's primary). */
  setActiveSiteId: (id: string | null) => void;
  /** Local-only state reset (also broadcast to other tabs). Does NOT call the API. */
  logout: () => void;
  updatePlan: (plan: PlanTier) => void;
}

const UNAUTH: Omit<
  AuthState,
  'hydrate' | 'setImpersonating' | 'setActiveSiteId' | 'logout' | 'updatePlan' | 'status' | 'isAuthenticated' | 'impersonating'
> = {
  userId: null,
  tenantId: null,
  tenantSlug: null,
  role: null,
  userRole: null,
  plan: null,
  sitePlan: null,
  bltEnabled: false,
  htmlAccess: false,
  limitedAccess: NOT_LIMITED,
  email: null,
  activeSiteId: null,
};

// ── Cross-tab auth signalling via BroadcastChannel ───────────────────────────
// One tab logging out (or a 401 that can't refresh) tells the others to drop auth. Impersonation
// rides the same channel: the cookies are shared by every tab in the browser, but this store is
// per-tab, so a tab that was already open when impersonation started would show no "Test Mode"
// band while its requests were, in fact, the customer's. It would also send the wrong CSRF token.
const authChannel: BroadcastChannel | null =
  typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('rs_auth') : null;

/**
 * Everything held in memory or storage that belongs to the ACCOUNT you were just in.
 *
 * Logout is the obvious caller. The one that made this a function is impersonation: entering or
 * leaving an account swaps the whole identity underneath a running app without going anywhere
 * near `logout`, and each of these outlives that swap on its own terms —
 *
 *   • the builder store holds the previous tenant's site config, navigation and page selection,
 *     which the editor can autosave onto the NEW tenant's site;
 *   • `bls.buildWizard.v1` is a single global localStorage key holding the previous tenant's
 *     site id and applied template, which the wizard resumes from — on the NEXT account's
 *     session, pointed at a site that account cannot see;
 *   • the intake draft is the same story in sessionStorage.
 *
 * Kept as one function so the next thing that needs dropping is added once rather than in the
 * four places that start or stop an impersonation.
 */
/**
 * Which account the PERSISTED account state (wizard progress in localStorage, the intake draft in
 * sessionStorage) belongs to. The in-memory identity cannot answer that on a cold tab: it starts
 * empty, so the first sign-in always looks like a "first identity" rather than a change, and would
 * inherit whatever the previous account in this browser left behind. Written on every successful
 * hydrate; a hydrate for a DIFFERENT recorded owner clears that state first.
 */
const ACCOUNT_OWNER_KEY = 'bls.accountOwner';
function readAccountOwner(): string | null {
  try { return localStorage.getItem(ACCOUNT_OWNER_KEY); } catch { return null; }
}
function writeAccountOwner(owner: string): void {
  try { localStorage.setItem(ACCOUNT_OWNER_KEY, owner); } catch { /* ignore */ }
}

/**
 * Browser-storage keys holding ONE account's in-progress data under a single global name — the
 * next account in this browser would resume it. Cleared on every account change.
 *
 * Keys scoped by tenant/site in their own name (studio undo, chat conversations, pending
 * questions) and plain UI preferences (smart mode, model choice, sidebar) are not here, on
 * purpose. src/store/accountStorageKeys.test.ts fails on any `bls` key that is in neither list, so
 * a new one has to be classified before it ships.
 */
export const ACCOUNT_SCOPED_STORAGE_KEYS = [
  'bls.buildWizard.v1', // build wizard progress: site id, steps (also cleared via clearBuildProgress)
  'bls.setupWizard.v1', // setup wizard progress
  'bls_template_wizard_v1', // template wizard: pages, MLS/API and office integration values
  'bls.mlsWizard.v1', // MLS connection wizard: the MLS picked and request details
] as const;
/** The same, in sessionStorage — which survives a reload in the tab, and so an account switch in it. */
export const ACCOUNT_SCOPED_SESSION_KEYS = [
  'bls-intake-store', // intake draft
  'bls.buildInterview.v1', // the pre-#750 guided interview key: no owner, so dropped, never adopted
  'bls.setupWizardHandoff.v1', // the wizard's "Build my site" description, read once by the Studio
] as const;

function resetTenantScopedState({ signingOut = false }: { signingOut?: boolean } = {}): void {
  useBuilderStore.getState().reset();
  useIntakeStore.getState().reset();
  clearBuildProgress();
  for (const key of ACCOUNT_SCOPED_STORAGE_KEYS) {
    try { localStorage.removeItem(key); } catch { /* ignore (private mode / SSR) */ }
  }
  for (const key of ACCOUNT_SCOPED_SESSION_KEYS) {
    try { sessionStorage.removeItem(key); } catch { /* ignore */ }
  }
  // Interview drafts are keyed by account (lib/interviewPersist), so they cannot cross an account
  // switch and survive one on purpose: an admin who leaves Test Mode and comes back to the same
  // agent resumes where they were. Signing out drops them all.
  if (signingOut) clearAllInterviews();
}

/**
 * Identity reads can overlap — another tab entering and then leaving Test Mode sends two
 * `impersonation-changed` messages, and each starts a hydrate — and their responses can arrive in
 * either order. Without a version, the OLDER answer ("impersonating as the agent") could land last
 * and overwrite the newer one, leaving the band up and the impersonation CSRF token selected after
 * the cookies were already gone. Every hydrate takes a generation; one that has been overtaken
 * applies nothing and resolves to the newest call's result instead.
 */
let hydrateGeneration = 0;
let latestHydrate: Promise<boolean> = Promise.resolve(false);

/**
 * Supersede every hydrate in flight WITHOUT starting a new one — for logout. The superseded calls
 * resolve to `false` (signed out) and apply nothing, instead of repopulating the identity a logout
 * just cleared.
 */
function invalidateInFlightHydrates(): void {
  hydrateGeneration += 1;
  latestHydrate = Promise.resolve(false);
}

async function hydrateAt(gen: number): Promise<boolean> {
  const set = useAuthStore.setState;
  const superseded = () => gen !== hydrateGeneration;
  const fetchMe = () =>
    fetch(`${API_URL}/auth/me`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
  /**
   * The request failed without the server answering the question. If this tab already holds an
   * identity, keep it — and with it the editor's unsaved work — rather than logging out over a
   * blip: the next call that reaches the server re-decides (a 401 refreshes or signs out, and
   * `x-impersonation-ended` / `impersonation_expired` re-hydrate). Only a tab with nothing to
   * keep (the cold-start hydrate, or one after a sign-in attempt) leaves `loading` for
   * `unauthenticated`, and even then it clears nothing: there is no account being left.
   */
  const keepOrDrop = (): false => {
    if (useAuthStore.getState().status !== 'authenticated') {
      set({ status: 'unauthenticated', isAuthenticated: false, impersonating: false, ...UNAUTH });
    }
    return false;
  };
  try {
    // Before the request goes out: a refresh completed after this moment means the 401 below was
    // answered to an older session than the cookie jar now holds.
    const sentAt = Date.now();
    let res = await fetchMe();
    // A cold load (reload / new deploy) runs BEFORE the keep-alive interval fires, so an expired
    // 15-min access token 401s here. This raw fetch bypassed the apiClient interceptor's
    // refresh-on-401, so a still-valid 7-day session was dropped straight to /login. Rotate the
    // session via the refresh cookie ONCE and retry — the fix for "reload always logs me out".
    if (res.status === 401) {
      let outcome = await refreshSessionOutcome(sentAt);
      // Once more when there was no answer: a blip may have passed, and when two tabs raced,
      // the other one may have refreshed meanwhile — this then reuses it rather than rotating.
      if (outcome === 'unavailable') outcome = await refreshSessionOutcome(sentAt);
      if (outcome === 'ok') res = await fetchMe();
      // The refresh never reached a verdict, so the 401 above is not one either — the session may
      // well be alive. Same rule as a failed request: nothing held is dropped.
      else if (outcome === 'unavailable') {
        if (superseded()) return latestHydrate;
        return keepOrDrop();
      }
    }
    // /auth/me is a raw fetch, so the axios interceptor never sees this header: a lapse found HERE
    // (a reload, a peer's message) would otherwise be known to this tab only.
    if (res.headers?.get?.('x-impersonation-ended') === '1') announceLapse();
    if (superseded()) return latestHydrate;
    // Anything but a definitive "no" (a 5xx, a 429, a gateway hiccup) is not an answer about
    // the session, so it must not cost anyone their work — see keepOrDrop below.
    if (!res.ok && res.status !== 401 && res.status !== 403) return keepOrDrop();
    if (!res.ok) {
      // Definitive: the refresh was tried and the server still says no. Drop the account's
      // state now, because the identity is about to become null — and once it is, the next
      // successful login sees no identity CHANGE and nothing else would ever clear it.
      resetTenantScopedState({ signingOut: true });
      set({ status: 'unauthenticated', isAuthenticated: false, impersonating: false, ...UNAUTH });
      return false;
    }
    const body = (await res.json()) as { data: MeResponse };
    if (superseded()) return latestHydrate;
    const { user, tenant, role, impersonating, limited_access } = body.data;
    if (!user) {
      resetTenantScopedState({ signingOut: true });
      set({ status: 'unauthenticated', isAuthenticated: false, impersonating: false, ...UNAUTH });
      return false;
    }
    // A DIFFERENT identity than this tab was holding (impersonation start/stop, or a switch)
    // must not keep the previous one's site selected: `activeSiteId` goes out as X-Site-Id on
    // every call, so carrying it over asks tenant B's session for tenant A's site.
    //
    // The USER counts, not only the tenant: an admin can impersonate a teammate inside their
    // own tenant, and that is still somebody else's session — their wizard progress and intake
    // draft are theirs. A first identity (nothing held yet) is not a change.
    const prev = useAuthStore.getState();
    const identityChanged =
      (!!prev.tenantId && prev.tenantId !== (tenant?.id ?? null)) ||
      (!!prev.userId && prev.userId !== user.id);
    // Not just the selected site: the editor's in-memory draft and the wizard's stored progress
    // belong to the account being left, and both can be written into the one being entered.
    // The persisted state's recorded owner, for the cold-tab case the in-memory check cannot see.
    // Saved state is kept only when it is RECORDED as this account's. State with no recorded owner
    // is cleared, not adopted (review on #750, round 9): it could be anyone's — the template
    // wizard's among it holds MLS/API and office integration values — and the browser most likely
    // to hold another account's is exactly the admin's, where Test Mode runs. The cost is one reset
    // of any in-progress wizard, on the first sign-in after this ships; the wizard's work itself is
    // saved on the server, what resets is the browser's place in it.
    const owner = `${tenant?.id ?? ''}:${user.id}`;
    const recordedOwner = readAccountOwner();
    const persistedForSomeoneElse = recordedOwner !== owner;
    if (identityChanged || persistedForSomeoneElse) resetTenantScopedState();
    writeAccountOwner(owner);
    set({
      status: 'authenticated',
      isAuthenticated: true,
      // The server's answer wins over whatever this tab last believed — including on the
      // hydrate that follows impersonate-STOP, where it is what turns the banner off.
      // A gateway that does not send the field (an older one, mid rolling deploy) has not said
      // "not impersonating" — it has said nothing. Keep what this tab already knew: an ordinary
      // session stays false, and one that has just entered Test Mode keeps its band and its
      // impersonation CSRF token rather than having both silently dropped.
      impersonating: typeof impersonating === 'boolean' ? impersonating : prev.impersonating,
      ...(identityChanged ? { activeSiteId: null } : {}),
      userId: user.id,
      email: user.email,
      userRole: user.role ?? null,
      tenantId: tenant?.id ?? null,
      tenantSlug: tenant?.slug ?? null,
      plan: (tenant?.plan as PlanTier) ?? null,
      sitePlan: tenant?.site_plan ?? tenant?.plan ?? null,
      bltEnabled: tenant?.blt_enabled === true,
      htmlAccess: tenant?.html_access === true,
      limitedAccess: parseLimitedAccess(limited_access),
      role: (role as TenantRole) ?? null,
    });
    return true;
  } catch {
    if (superseded()) return latestHydrate;
    // A thrown request (offline, DNS, CORS during a deploy) says nothing about the session.
    return keepOrDrop();
  }
}

/**
 * Tell the other tabs that the identity behind the shared cookies changed, so each re-reads it
 * from the server. For transitions this tab did not start through `setImpersonating` — above
 * all the gateway reporting that an impersonation LAPSED. Receivers hydrate and never
 * re-announce (see onmessage below), so there is no echo loop.
 */
export function announceIdentityChanged(): void {
  authChannel?.postMessage({ type: 'impersonation-changed' });
}

/**
 * A response said the impersonation behind the shared cookies has LAPSED (the gateway sets
 * `x-impersonation-ended` on any request that carried a dead impersonation cookie, including the
 * 403 `impersonation_expired` on a write). Re-read identity here and tell the other tabs.
 *
 * Throttled: a tab never announces a lapse more than once per few seconds. Cookies are cleared by
 * the first such response, so peers re-reading find nothing — but if a clear ever failed to land,
 * every re-read would carry the header again, and unthrottled tabs would bounce the message
 * between them forever.
 */
const LAPSE_NOTICE_INTERVAL_MS = 5_000;
let lastLapseNotice = 0;
function announceLapse(): void {
  const now = Date.now();
  if (now - lastLapseNotice < LAPSE_NOTICE_INTERVAL_MS) return;
  lastLapseNotice = now;
  announceIdentityChanged();
}
export function noteImpersonationEnded(): Promise<boolean> {
  announceLapse();
  return resyncIdentity();
}

/**
 * Re-read identity, giving up after a bound. For the moments that WAIT on the answer before going
 * on — leaving or entering Test Mode, and a response held because the impersonation behind it
 * lapsed. An `/auth/me` that never answers must not leave the exit button spinning, or a request
 * hanging, for ever: past the bound this resolves `false` ("not confirmed"), and the callers take
 * their safe path (a full reload, or withholding the response). The re-read itself carries on and
 * still applies whatever it gets back.
 */
export const RESYNC_TIMEOUT_MS = 8_000;
export function resyncIdentity(timeoutMs: number = RESYNC_TIMEOUT_MS): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    const timer = setTimeout(() => resolve(false), timeoutMs);
    useAuthStore.getState().hydrate().then(
      (ok) => { clearTimeout(timer); resolve(ok); },
      () => { clearTimeout(timer); resolve(false); },
    );
  });
}
/** Tests only. */
export function resetLapseThrottleForTests(): void {
  lastLapseNotice = 0;
}

export const useAuthStore = create<AuthState>()((set) => ({
  status: 'loading',
  isAuthenticated: false,
  impersonating: false,
  ...UNAUTH,

  hydrate: () => {
    const gen = ++hydrateGeneration;
    const run = hydrateAt(gen);
    latestHydrate = run;
    return run;
  },

  setImpersonating: (value: boolean) => {
    set({ impersonating: value });
    // Tell the other tabs to re-read identity from the server rather than guess. They hydrate
    // instead of copying the flag, so `impersonating` still has exactly one source (see hydrate).
    authChannel?.postMessage({ type: 'impersonation-changed' });
  },

  setActiveSiteId: (id: string | null) => set({ activeSiteId: id }),

  logout: () => {
    // A hydrate already in flight must not land AFTER this and sign the tab back in.
    invalidateInFlightHydrates();
    resetTenantScopedState({ signingOut: true });

    set({ status: 'unauthenticated', isAuthenticated: false, impersonating: false, ...UNAUTH });
    // Tell other tabs to drop auth too (guard against echo loops with a type tag).
    authChannel?.postMessage({ type: 'logout' });
  },

  updatePlan: (plan: PlanTier) => set({ plan, sitePlan: plan }),
}));

// Other-tab logout: clear local auth WITHOUT re-broadcasting (avoid an echo loop).
if (authChannel) {
  authChannel.onmessage = (e: MessageEvent) => {
    if (e.data?.type === 'impersonation-changed') {
      // Another tab entered or left an account. Re-hydrate so this tab's banner, CSRF choice and
      // tenant identity match the shared cookies instead of what it happened to load with.
      void useAuthStore.getState().hydrate();
      return;
    }
    // Another tab signed out: a hydrate in flight HERE must not land afterwards and sign this tab
    // back in — whatever this tab's status is at the moment (a cold 'loading' one included).
    if (e.data?.type === 'logout') invalidateInFlightHydrates();
    if (e.data?.type === 'logout' && useAuthStore.getState().status !== 'unauthenticated') {
      resetTenantScopedState({ signingOut: true });
      useAuthStore.setState({
        status: 'unauthenticated',
        isAuthenticated: false,
        impersonating: false,
        ...UNAUTH,
      });
    }
  };
}
